import { ApplicationConfig, isDevMode, inject, provideAppInitializer, ErrorHandler, provideZonelessChangeDetection } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withComponentInputBinding, withRouterConfig, withViewTransitions } from '@angular/router';
import { tap } from 'rxjs';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { provideUniPinGql } from '@unipin/angular-gql';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { environment } from '@env/environment';
import { routes } from './app.routes';
import { TranslocoHttpLoader } from './transloco.loader';
import { redirectTo, redirectTo404 } from '@core/utils/route-redirector.util';
import { AppStore } from '@core/states/app/app.store';
import { interceptHeader } from '@core/interceptors/header.interceptor';
import { WebtopupService } from '@core/services/webptopup.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideZonelessChangeDetection(),
    provideHttpClient(
      withInterceptors([ interceptHeader ])
    ),
    provideRouter(
      routes,
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withViewTransitions(),
      withComponentInputBinding()
    ),

    provideUniPinGql({
      useOperationPath: localStorage.getItem('disableOperationPath')? false : true,
      uri: location.href.includes('unibox-in') || location.href.includes('india')? environment.indiaGqlUrl : environment.gqlUrl,
    }),

    provideTransloco({
      loader: TranslocoHttpLoader,
      config: {
        defaultLang: 'en',
        fallbackLang: 'en',
        prodMode: !isDevMode(),
        reRenderOnLangChange: false,
        flatten: {
          aot: !isDevMode()
        },
        missingHandler: {
          allowEmpty: true,
          useFallbackTranslation: true
        },
        availableLangs: [
          'ar',
          'bn',
          'br',
          'ch',
          'en',
          'es',
          'hi',
          'id',
          'kh',
          'ko',
          'lo',
          'mm',
          'ms',
          'nl',
          'ph',
          'ru',
          'si',
          'sv',
          'th',
          'tr',
          'ur',
          'vn',
          'zhhk',
          'zhtw',
          'zu',
        ]
      }
    }),

    {
      provide: ErrorHandler,
      useValue: createErrorHandler(),
    },
    {
      provide: TraceService,
      deps: [Router],
    },

    provideAppInitializer(() => {
      const router = inject(Router);
      const store = inject(AppStore);
      const service = inject(WebtopupService);
      const transloco = inject(TranslocoService);

      const guids = location.pathname.substring(1).split('/');
      if (!guids || guids.length < 2) {
        redirectTo404(router, '', '');
        return;
      }

      transloco.setActiveLang(
        (new URL(window.location.href)).searchParams.get('lang') || 'en'
      );

      const guid = guids.pop();
      return service.inquiry(
        { 
          operationName: 'getCustomStyle',
          variables: { 
            txnGuid: guid!
          }, 
        },
        'customStyle'
      ).pipe(
        tap(({ data: { pgwMsInquiryAction: data }}) => {
          store.setCustomStyling(data.customStyle);
          redirectTo(router, guid!, data);
        })
      );
    })
  ]
};
